import React, { useCallback, useRef, type KeyboardEvent, type PointerEvent } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { timelineMessages } from '../messages/timelineMessages';
import type { TimelineView } from './timelineGeometry';
import styles from './TimelineScroll.module.scss';

interface TimelineScrollProps {
  view: TimelineView;
  range: { startSec: number; endSec: number };
  onScroll: (from: number) => void;
}

/**
 * The scroll bar of a zoomed timeline: the thumb is the visible part of the video. A drag of the
 * thumb scrolls; a click on the track centres the view there; the arrow keys scroll a tenth.
 */
const TimelineScrollComponent: React.FC<TimelineScrollProps> = ({ view, range, onScroll }) => {
  const intl = useIntl();
  const trackRef = useRef<HTMLDivElement>(null);
  const grab = useRef<{ pointerId: number; startX: number; from: number } | null>(null);
  const whole = Math.max(range.endSec - range.startSec, 1);
  const span = view.to - view.from;
  const left = ((view.from - range.startSec) / whole) * 100;
  const width = (span / whole) * 100;

  const secPerPx = useCallback((): number => {
    const track = trackRef.current;
    const trackWidth = track === null ? 0 : track.getBoundingClientRect().width;
    return trackWidth > 0 ? whole / trackWidth : 0;
  }, [whole]);

  const onTrackDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const track = trackRef.current;
      if (track === null || event.target !== track) {
        return;
      }
      const rect = track.getBoundingClientRect();
      const sec = range.startSec + ((event.clientX - rect.left) / rect.width) * whole;
      onScroll(sec - span / 2);
    },
    [onScroll, range.startSec, span, whole]
  );

  const onThumbDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      grab.current = { pointerId: event.pointerId, startX: event.clientX, from: view.from };
    },
    [view.from]
  );

  const onThumbMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const { current } = grab;
      if (current === null || current.pointerId !== event.pointerId) {
        return;
      }
      onScroll(current.from + (event.clientX - current.startX) * secPerPx());
    },
    [onScroll, secPerPx]
  );

  const onThumbUp = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    grab.current = null;
  }, []);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      onScroll(view.from + (event.key === 'ArrowLeft' ? -1 : 1) * span * 0.1);
    },
    [onScroll, span, view.from]
  );

  return (
    <div ref={trackRef} className={styles.track} onPointerDown={onTrackDown}>
      <div
        role="scrollbar"
        tabIndex={0}
        aria-orientation="horizontal"
        aria-label={intl.formatMessage(timelineMessages.scroll)}
        aria-controls="music-video-timeline-lanes"
        aria-valuemin={Math.round(range.startSec)}
        aria-valuemax={Math.round(range.endSec - span)}
        aria-valuenow={Math.round(view.from)}
        className={styles.thumb}
        style={{ left: `${left}%`, width: `${width}%` }}
        onPointerDown={onThumbDown}
        onPointerMove={onThumbMove}
        onPointerUp={onThumbUp}
        onPointerCancel={onThumbUp}
        onKeyDown={onKeyDown}
      />
    </div>
  );
};

export const TimelineScroll = TypedMemo(TimelineScrollComponent);
